import { useCallback } from 'react';

import { getLogoutUrl } from '@/shared/api/base';
import { env } from '@/shared/lib/config/env';
import { clearAuthBlockState, removeLocalStorageItem } from '@/shared/utils';

const flowaiLogoutTargets = [
  'https://flowai.ru/logout-listener.html',
  'https://rnd.flowai.ru/logout-listener.html',
];

const clearAuthSession = () => {
  clearAuthBlockState();
  removeLocalStorageItem('token');
  removeLocalStorageItem('pkce_code_verifier');
};

const notifyFlowaiLogoutTargets = async () => {
  const logoutOperationId = `logout_${Date.now()}_${Math.random()}`;

  const promises = flowaiLogoutTargets.map((src) => {
    return new Promise<void>((resolve) => {
      const iframe = document.createElement('iframe');
      const targetOrigin = new URL(src).origin;
      let completed = false;

      const finalize = () => {
        if (completed) {
          return;
        }

        completed = true;
        window.removeEventListener('message', handleMessage);
        iframe.remove();
        resolve();
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== targetOrigin) return;
        if (event.data?.type === 'LOGOUT_ACK' && event.data?.id === logoutOperationId) {
          finalize();
        }
      };

      iframe.style.display = 'none';
      iframe.src = src;

      window.addEventListener('message', handleMessage);

      iframe.onload = () => {
        iframe.contentWindow?.postMessage({ type: 'LOGOUT', id: logoutOperationId }, targetOrigin);
      };

      document.body.appendChild(iframe);

      // safety timeout in case ACK never arrives
      setTimeout(finalize, 2000);
    });
  });

  await Promise.all(promises);
};

export const useHandleLogout = () => {
  const handleLogout = useCallback(async () => {
    if (env.VITE_AUTH_PROVIDER === '3d') {
      await notifyFlowaiLogoutTargets();
    }

    clearAuthSession();
    window.location.href = await getLogoutUrl();
  }, []);

  return { handleLogout };
};
